import type { ColumnDef } from '@tanstack/react-table';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/atoms';
import { DataTable } from '@/components/molecules';
import {
  formatProductImageIds,
  parseProductImageIds,
} from '@/lib/helpers/product-images';
import type {
  BulkUploadSpreadsheetRow,
  BulkUploadValidatedRow,
  BulkUploadValidationData,
} from '@/types';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { MediaLibraryMultiSelect } from '../../components/media-library-multi-select';

interface BulkUploadValidationStepProps {
  validation: BulkUploadValidationData;
  rows: BulkUploadSpreadsheetRow[];
  isSubmitting: boolean;
  isRevalidating: boolean;
  onRowsChange: (rows: BulkUploadSpreadsheetRow[]) => void;
  onBack: () => void;
  onRevalidate: () => void;
  onImport: () => void;
}

function getSpreadsheetRowIndex(validatedRowNumber: number): number {
  return validatedRowNumber - 2;
}

export function BulkUploadValidationStep({
  validation,
  rows,
  isSubmitting,
  isRevalidating,
  onRowsChange,
  onBack,
  onRevalidate,
  onImport,
}: BulkUploadValidationStepProps) {
  const hasValidRows = validation.validCount > 0;
  const allValid = validation.errorCount === 0;

  const updateRowImages = (validatedRowNumber: number, imageIds: number[]) => {
    const rowIndex = getSpreadsheetRowIndex(validatedRowNumber);
    if (rowIndex < 0 || rowIndex >= rows.length) return;

    const nextRows = [...rows];
    nextRows[rowIndex] = {
      ...nextRows[rowIndex],
      'Product Images': formatProductImageIds(imageIds),
    };
    onRowsChange(nextRows);
  };

  const columns: ColumnDef<BulkUploadValidatedRow>[] = [
    {
      accessorKey: 'row',
      header: 'Row',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.row}</span>
      ),
    },
    {
      accessorKey: 'productName',
      header: 'Product',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.productName || '—'}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) =>
        row.original.status === 'valid' ? (
          <Badge variant="success">Valid</Badge>
        ) : (
          <Badge variant="destructive">Error</Badge>
        ),
    },
    {
      id: 'images',
      header: 'Product Images',
      cell: ({ row }) => {
        const rowIndex = getSpreadsheetRowIndex(row.original.row);
        const spreadsheetRow = rows[rowIndex];
        const selectedIds = parseProductImageIds(
          spreadsheetRow?.['Product Images']
        );

        return (
          <div className="min-w-[220px]">
            <MediaLibraryMultiSelect
              value={selectedIds}
              onChange={(imageIds: number[]) =>
                updateRowImages(row.original.row, imageIds)
              }
              placeholder="Attach images"
            />
          </div>
        );
      },
    },
    {
      id: 'errors',
      header: 'Issues',
      cell: ({ row }) => {
        if (row.original.errors.length === 0) {
          return (
            <span className="text-sm text-muted-foreground">
              Ready to import
            </span>
          );
        }

        return (
          <ul className="space-y-2">
            {row.original.errors.map((error) => (
              <li key={`${error.field}-${error.message}`} className="text-sm">
                <span className="font-medium">{error.field}:</span>{' '}
                {error.message}
                {error.suggestion ? (
                  <span className="mt-1 block text-muted-foreground">
                    Suggestion: {error.suggestion}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <Card className="border shadow-none">
        <CardHeader className="gap-4 border-b pb-6">
          <CardTitle className="text-lg">Validation results</CardTitle>
          <p className="text-muted-foreground text-sm">
            Attach images from your media library for each product, then click
            Re-validate to save your draft and run validation again.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border bg-muted/20 p-4">
              <p className="text-sm text-muted-foreground">Total rows</p>
              <p className="text-2xl font-semibold">{validation.totalRows}</p>
            </div>
            <div className="rounded-lg border bg-green-50 p-4">
              <p className="text-sm text-green-800">Valid products</p>
              <p className="text-2xl font-semibold text-green-900">
                {validation.validCount}
              </p>
            </div>
            <div className="rounded-lg border bg-red-50 p-4">
              <p className="text-sm text-red-800">Rows with errors</p>
              <p className="text-2xl font-semibold text-red-900">
                {validation.errorCount}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {validation.missingColumns.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              Missing required columns: {validation.missingColumns.join(', ')}
            </div>
          )}

          <div className="overflow-x-auto rounded-xl border bg-white">
            <DataTable columns={columns} data={validation.rows} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              {allValid ? (
                <>
                  <CheckCircle2 className="mt-0.5 size-4 text-primary" />
                  <span>
                    All rows passed validation and are ready to import.
                  </span>
                </>
              ) : (
                <>
                  <AlertCircle className="mt-0.5 size-4 text-destructive" />
                  <span>
                    Attach images and re-validate, or import valid rows and fix
                    failed ones separately.
                  </span>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button
                type="button"
                variant="outline"
                isLoading={isRevalidating}
                onClick={onRevalidate}
              >
                Re-validate
              </Button>
              <Button
                type="button"
                isLoading={isSubmitting}
                disabled={!hasValidRows}
                onClick={onImport}
              >
                Import {validation.validCount} product
                {validation.validCount === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
